import { useEffect, useState } from "react";
import { Badge, Button } from "react-bootstrap";
import { FaInfoCircle } from "react-icons/fa";
import { getQuoteTotals } from "../../services/apiClient.js";
import Hint from "./../shared/Hint.jsx";

// Grand Totals (legacy GrandTotalsHelper) as ONE stat strip:
//   COST + MARKUP = SELL  │  ACTUAL COST · PROFIT · GIVEN · VARIANCE
// Sell is the hero; profit is the second hero (green/red). The actuals (payable
// side, from Orders) show AUTOMATICALLY whenever the job has any — no toggle to
// miss. Sources: estimates + variations + PC sums, a Final Quote override, or
// variations + PC sums only on Do & Charge-style jobs. Reflects SAVED data; ↻
// re-pulls after grid edits.

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));

function Stat({ label, value, size = "fs-5", className = "", sub = null, hint = null }) {
  return (
    <div data-hint={hint || undefined}>
      <div className="text-muted text-uppercase" style={{ fontSize: "0.68rem", letterSpacing: "0.06em" }}>
        {label}
      </div>
      <div className={`${size} fw-semibold lh-sm text-nowrap ${className}`.trim()}>
        {value}
        {sub ? <span className="fs-6 fw-normal text-muted ms-1">{sub}</span> : null}
      </div>
    </div>
  );
}

// A quiet operator between stats, baseline-aligned with the values.
const Op = ({ children }) => (
  <div className="fs-4 text-body-tertiary align-self-end lh-sm" aria-hidden="true">{children}</div>
);

const SOURCE_NOTE = {
  sum: "estimates + variations + PC sums",
  non_quote: "variations + PC sums (Do & Charge-style job)",
};

function GrandTotals({ claimId, reloadKey }) {
  const [t, setT] = useState(null);
  const [error, setError] = useState("");

  function load() {
    getQuoteTotals(claimId)
      .then(setT)
      .catch(() => setError("Unable to load totals."));
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, reloadKey]);

  const markupPct = t && t.cost ? (t.markup / t.cost) * 100 : 0;
  // Show actuals whenever the payable side has anything — if it exists, show it.
  const hasActuals =
    t?.actuals && (Number(t.actuals.actual_cost) > 0 || Number(t.actuals.contract_given) > 0);
  // A young job (Requested etc.) with no priced lines: the full stat strip is
  // noise — one quiet sentence instead (Robert 2026-07-15).
  const isEmpty =
    t && !hasActuals && !Number(t.cost) && !Number(t.sell) && t.source !== "final_quote";

  return (
    <div>
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* ONE row: title + source + refresh anchor the left, the stat strip rides
          right — no separate header row (wraps gracefully when squeezed).
          EMPTY state: title + sentence stay TOGETHER on the left as one quiet
          line (right-aligned it floated under the Go-to bar — Robert 2026-07-15). */}
      <div className={`d-flex align-items-center column-gap-4 row-gap-3 flex-wrap ${isEmpty ? "" : "justify-content-between"}`}>
        <div className="d-flex align-items-center gap-2">
          <h6 className="text-uppercase fw-semibold small mb-0" style={{ letterSpacing: "0.06em" }}>
            Grand Totals
          </h6>
          {t && !isEmpty ? (
            t.source === "final_quote" ? (
              <Badge bg="warning" text="dark">Final Quote override</Badge>
            ) : (
              // What the totals SUM — the bare Σ read as a glitch (Robert
              // 2026-07-16), so it wears the app's standard info icon.
              <Hint label={`Summing: ${SOURCE_NOTE[t.source] || SOURCE_NOTE.sum}`}>
                <span className="text-info d-inline-flex align-middle" style={{ cursor: "default", fontSize: "0.85rem" }}>
                  <FaInfoCircle />
                </span>
              </Hint>
            )
          ) : null}
          {!isEmpty ? (
            <Hint label="Re-pull saved totals">
              <Button size="sm" variant="outline-secondary" className="py-0 px-1" onClick={load}>↻</Button>
            </Hint>
          ) : null}
        </div>

        {isEmpty ? (
          <span className="small text-muted">— no priced lines yet; totals appear as the quote is built.</span>
        ) : t ? (
          <div className="d-flex align-items-end column-gap-4 row-gap-3 flex-wrap">
            <Stat label="Total Cost" value={money(t.cost)} />
            <Op>+</Op>
            <Stat label="Markup" value={money(t.markup)} sub={`(${markupPct.toFixed(1)}%)`} />
            <Op>=</Op>
            <Stat label="Total Quote (Sell)" value={money(t.sell)} size="fs-4" className="text-primary fw-bold" />

            {hasActuals ? (
              <>
                <div className="vr align-self-stretch mx-2" />
                <Stat
                  label="Actual Cost"
                  value={money(t.actuals.actual_cost)}
                  size="fs-5"
                  hint="Σ contractor invoices received, from the Orders tab"
                />
                <Stat
                  label="Profit to Date"
                  value={money(t.actuals.profit)}
                  size="fs-4"
                  className={t.actuals.profit < 0 ? "text-danger" : "text-success"}
                  hint="Sell − actual contractor cost"
                />
                <Stat
                  label="Contract Given"
                  value={money(t.actuals.contract_given)}
                  size="fs-5"
                  hint="Σ committed contract lines, from the Orders tab"
                />
                <Stat
                  label="Variance vs Given"
                  value={money(t.actuals.variance)}
                  size="fs-5"
                  className={t.actuals.variance > 0 ? "text-danger" : "text-success"}
                  sub={t.actuals.variance > 0 ? "(over committed)" : null}
                  hint="Actual cost − contract given (red = invoiced beyond the committed amount)"
                />
              </>
            ) : null}
          </div>
        ) : (
          <span className="text-muted">Loading…</span>
        )}
      </div>
    </div>
  );
}

export default GrandTotals;
